<template>
	<NuxtLayout
		name="slideshow"
		:slides="homepageSlides"
		:background-video="{
			mp4Src: cdnUrl('fit-hier-homepage-header_1080p_v1.mp4'),
			webmSrc: cdnUrl('fit-hier-homepage-header_1080p_v1.webm'),
			posterImg: {
				src: '/img/fitness/small-group-training_01.jpg',
				alt: 'Fit Hier Small Group Training',
				preload: true,
				width: 1920,
				height: 663,
			},
		}"
		page-title-prefix="Begeleid sporten"
		page-title="Sporten met een trainer"
		page-title-suffix="Als je wil sporten met persoonlijke begeleiding en voor echt resultaat gaat!"
	>
		<AtomScrollContainer class="lg:container">
			<div
				v-for="subscriptionGroup in subscriptionGroups"
				:key="`subscription-card-${subscriptionGroup.id}`"
				class="lg:px-2 xl:px-6 py-[5vw] lg:w-1/3 shrink-0"
			>
				<OrganismSubscriptionCard
					data-testid="MembershipGroupCard"
					class="snap-center h-full w-[87vw] md:w-[50vw] lg:w-full"
					:title="subscriptionGroup.title"
					:label="subscriptionGroup.label"
					:usps="subscriptionGroup.usps"
					:to="{
						title: subscriptionGroup.title,
						url: subscriptionGroup.url,
					}"
					:button-text="subscriptionGroup.buttonText"
					:secondary-button-to="subscriptionGroup.secondaryButtonText && subscriptionGroup.secondaryUrl ? {
						title: subscriptionGroup.secondaryButtonText,
						url: subscriptionGroup.secondaryUrl,
					} : undefined"
					:secondary-button-text="subscriptionGroup.secondaryButtonText"
				/>
			</div>
		</AtomScrollContainer>

		<div class="flex flex-col gap-36">
			<div class="container -mt-20">
				<BannerZft />
			</div>
			<div class="container">
				<div class="bg-gray-100 rounded-lg p-16">
					<AtomTitle
						tag="h1"
						size="3xl"
						class="mb-8"
					>
						Trainen met focus op de individu
					</AtomTitle>
					<p class="text-paragraph-size-lg md:text-paragraph-size-xl">
						Bij Fit Hier draait alles om persoonlijke aandacht, kleinschaligheid en resultaat. We bieden hoge kwaliteit trainingen afgestemd op de individu. Of je nu net begint, al langer sport en wat je doel ook is. Train met ons voor een betere conditie, sterker lichaam en algehele fitheid.
					</p>
				</div>
			</div>

			<div class="container max-w-screen-md flex flex-col gap-16">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Waarom trainen bij Fit Hier?
				</AtomTitle>

				<div class="grid grid-cols-1 md:grid-cols-2 gap-12">
					<MoleculeSquareCard
						title="Exclusieve setting"
						text="Een rustige, overzichtelijke trainingsomgeving waar ruimte, sfeer en kwaliteit samenkomen. Geen rijen bij apparaten of drukte in de zaal, maar een plek waar jij je volledig kunt focussen op je training."
						:image="{
							src: '/img/fitness/loopband_02.jpg',
							alt: 'Loopband workout',
							width: 700,
							height: 700,
						}"
					/>
					<MoleculeSquareCard
						title="Kwaliteit & aandacht"
						text="Echte progressie begint bij persoonlijke begeleiding en oprechte aandacht. We stemmen de training af op jouw doelen, houden rekening met je belastbaarheid en passen het programma daarop aan. Behaal duurzaam resultaat, zowel fysiek als mentaal."
						:image="{
							src: '/img/fitness/squat-rack_01.jpg',
							alt: 'Squat workout',
							width: 700,
							height: 700,
						}"
					/>
					<MoleculeSquareCard
						title="Kleinschalige trainingsvormen"
						text="Iedereen krijgt de aandacht die nodig is dankzij onze persoonlijke aanpak. Je traint in kleine groepen tot zes personen, individueel, in duo of op maat met je sportteam of bedrijf. Zo ontstaat een doelgerichte en motiverende setting waarin je optimaal presteert."
						:image="{
							src: '/img/fitness/slee_04.jpg',
							alt: 'Slee workout',
							width: 700,
							height: 700,
						}"
					/>
					<MoleculeSquareCard
						title="Samen sterker als team"
						text="Van sportverenigingen tot bedrijventeams: we bieden trainingen op maat die bijdragen aan betere prestaties én teamgevoel. Door samen te trainen werk je niet alleen aan fysieke doelen, maar versterk je ook de onderlinge band."
						:image="{
							src: '/img/fitness/small-group-training_01.jpg',
							alt: 'Team training',
							width: 700,
							height: 700,
						}"
					/>
				</div>
			</div>
			<div class="container flex flex-col items-center gap-36 pb-36">
				<CompositionBannerFreeSgtTrial />

				<div class="flex flex-col gap-12">
					<AtomTitle
						tag="h2"
						size="3xl"
						class="text-center"
					>
						Onze leden aan het woord
					</AtomTitle>

					<OrganismReviewSlideshow :reviews="reviews" />
				</div>
			</div>
		</div>
	</NuxtLayout>
</template>

<script lang="ts" setup>
import homepageSlides from '#fit/core/consts/homepageSlides';
import { cdnUrl } from '#base/helpers/contentHelpers';
import { excersiseGymSchema } from '#fit/helpers/schemaHelpers';
import { formatSchema } from '#base/helpers/schemaHelpers';
import reviews from '#fit/core/consts/reviews';
import BannerZft from '~/components/composition/BannerZft.vue';

useSeoMeta({
	title: 'Jouw Privé sportschool in Uden | Fit Hier',
	ogTitle: 'Jouw Privé sportschool - Fit Hier',
	description:
        'Kom sporten met Small Group Training tot 6 personen, Personal Training of Private Group Training, altijd met een professionele trainer.',
});

useHead({
	script: [formatSchema(excersiseGymSchema)],
});

const {
	data: subscriptionGroups,
} = await useAsyncData('subscription_groups', () => queryCollection('subscription_groups')
	.all());
</script>
